import { apiCall, apiCallWithCsrfRefresh } from "./api";
import { getAnonPhysiologyDraft, setAnonPhysiologyDraft } from "./anonDraft";

/**
 * Customer physiology API — `sex` / `weight` / `weightUnit` on the `Customer`
 * row. These tune the fuel calculation and are distinct from the fueling
 * preferences (`CustomerPreferences`); they are written via a separate endpoint
 * so the two Prisma models stay un-conflated.
 *
 * Anon principals have no `Customer` row: `loadProfile` returns an all-null
 * profile and `saveProfile` 401s with `login_required` — callers should keep
 * anon physiology in the browser session.
 */

export type CustomerProfile = {
  sex: "male" | "female" | "non-binary" | "other" | null;
  weight: number | null;
  weightUnit: "kg" | "lb" | null;
  /** v4 onboarding — optional so existing constructors don't need updating. */
  experienceLevel?: "beginner" | "intermediate" | "advanced" | null;
  hintsDismissed?: boolean;
};

export type ProfileResponse = { profile: CustomerProfile };

/** Partial physiology patch — omit a key to leave it untouched, send null to clear it. */
export type SaveProfileInput = {
  sex?: "male" | "female" | "non-binary" | "other" | null;
  weight?: number | null;
  weightUnit?: "kg" | "lb" | null;
  experienceLevel?: "beginner" | "intermediate" | "advanced" | null;
  hintsDismissed?: boolean;
};

/** Empty physiology row — anon `GET /profile` resolves to this shape. */
export function emptyCustomerProfile(): CustomerProfile {
  return { sex: null, weight: null, weightUnit: null };
}

/**
 * Session physiology for anonymous principals (journey §9). PreferencesModal and
 * the onboarding manual-session modal both write here; merged to `Customer` on login.
 *
 * Backed by `localStorage` with a 28-day TTL (see `anonDraft.ts`) — survives tab
 * closes; legacy `sessionStorage` rows are migrated once.
 */
export function getAnonPhysiologySession(): CustomerProfile | null {
  return getAnonPhysiologyDraft<CustomerProfile>();
}

export function setAnonPhysiologySession(profile: CustomerProfile | null): void {
  setAnonPhysiologyDraft(profile);
}

/** Merge session physiology onto the anon API shape (`GET /profile` is all-null). */
export function overlayAnonPhysiologyOntoLoaded(apiProfile: CustomerProfile): CustomerProfile {
  const anon = getAnonPhysiologySession();
  if (!anon) return apiProfile;
  return {
    sex: anon.sex ?? apiProfile.sex,
    weight: anon.weight ?? apiProfile.weight,
    weightUnit: anon.weight != null ? anon.weightUnit : apiProfile.weightUnit
  };
}

/** Build a full physiology object from transient form fields — shared UX with PreferencesModal. */
export function physiologyFromInputs(input: {
  sex: CustomerProfile["sex"];
  weightText: string;
  weightUnit: "kg" | "lb";
}): CustomerProfile {
  const w = input.weightText.trim() === "" ? NaN : Number(input.weightText);
  const validWeight = Number.isFinite(w) && w > 0 ? w : null;
  return {
    sex: input.sex,
    weight: validWeight,
    weightUnit: validWeight != null ? input.weightUnit : null
  };
}

const BASE = "/api/customer/profile";

/**
 * Process-lifetime cache for `GET /profile` so independent screens (My Training,
 * Fueling Plan) and Strict-Mode double-mounts share one request instead of each
 * re-fetching. No TTL — and no staleness — because the only writer of the
 * `Customer` physiology row is `saveProfile` (every save path routes through
 * it), which refreshes the cache. Anon principals cache the all-null API shape;
 * their live values are layered from `localStorage` per consumer, so anon
 * freshness is independent of this cache.
 */
let cachedProfile: CustomerProfile | null = null;
let inflightProfile: Promise<CustomerProfile> | null = null;
// Bumped on every invalidation so a GET that was already in flight when the
// cache was invalidated can't write its now-stale result back into the cache.
let profileCacheGen = 0;

/** Drop the cached profile. Called on save and when the principal changes. */
export function invalidateProfileCache(): void {
  cachedProfile = null;
  inflightProfile = null;
  profileCacheGen += 1;
}

export async function loadProfile(
  signal?: AbortSignal,
  opts?: { force?: boolean }
): Promise<CustomerProfile> {
  void signal; // never threaded onto the shared request — see note below
  if (!opts?.force && cachedProfile) return cachedProfile;
  if (!inflightProfile) {
    // Never attach a caller's AbortSignal to the shared request: a fast unmount
    // (or React Strict Mode) would abort it for every other subscriber. Callers
    // that pass a signal guard their own setState; the payload is tiny, so a
    // late resolve after unmount is harmless.
    const gen = profileCacheGen;
    const promise = apiCall<ProfileResponse>(BASE)
      .then((res) => {
        const profile = res.profile ?? emptyCustomerProfile();
        // Only populate the cache if it wasn't invalidated mid-flight.
        if (gen === profileCacheGen) cachedProfile = profile;
        return profile;
      })
      .finally(() => {
        if (inflightProfile === promise) inflightProfile = null;
      });
    inflightProfile = promise;
  }
  return inflightProfile;
}

export async function saveProfile(
  input: SaveProfileInput,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<CustomerProfile> {
  const res = await apiCallWithCsrfRefresh<ProfileResponse & { saved?: boolean }>(
    BASE,
    { method: "PATCH", body: input, csrfToken },
    refreshCsrf
  );
  const profile = res.profile ?? emptyCustomerProfile();
  // The physiology row just changed server-side. Invalidate first (bumps the
  // generation so an in-flight pre-save GET can't clobber), then seed the cache
  // with the authoritative saved value so the next read is instant and fresh —
  // even on the ConnectContext save path, which doesn't fire the saved event.
  invalidateProfileCache();
  cachedProfile = profile;
  return profile;
}
